import React, { lazy, Suspense } from "react";
import { createBrowserHistory } from "history";
import { Route, Router, Switch } from "react-router-dom";

import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import ReactGA from "react-ga4";

import PageMiss from "./404/404";
import Loader from "./Utils/Loader/Loader";

import "./App.css";

const Home = lazy(() => import(/* webpackChunkName: "home" */ "./Home/Home"));
const Team = lazy(() => import(/* webpackChunkName: "team" */ "./Team/Team"));
const Faculty = lazy(() => import(/* webpackChunkName: "faculty" */ "./Faculty/Faculty"));

const PreConferenceWorkshop = lazy(
  () => import(/* webpackChunkName: "preconferenceworkshop" */ "./PreConferenceWorkshop/PreConferenceWorkshop.js")
);

const Partners = lazy(() => import(/* webpackChunkName: "partners" */ "./Partners/Partners"));
const Abstract = lazy(() => import(/* webpackChunkName: "abstract" */ "./Abstract/Abstract"));
const ScientificProgram = lazy(() => import(/* webpackChunkName: "scientific-program" */ "./ScientificProgram/ScientificProgram"));
const ScientificSchedule = lazy(() => import(/* webpackChunkName: "scientific-schedule" */ "./ScientificSchedule/ScientificSchedule"));
const OrderSuccess = lazy(() => import(/* webpackChunkName: "order-success" */ "./OrderStatus/OrderSuccess"));
const OrderFail = lazy(() => import(/* webpackChunkName: "order-failed" */ "./OrderStatus/OrderFail"));
const Contact = lazy(() => import(/* webpackChunkName: "contact" */ "./Contact/Contact"));
const NearbyPlaces = lazy(() => import(/* webpackChunkName: "nearby-places" */ "./NearbyPlaces/NearbyPlaces"));
const Registration = lazy(() => import(/* webpackChunkName: "registration" */ "./Registration/Registration"));
const Industry = lazy(() => import(/* webpackChunkName: "industry" */ "./Reg-Routes/Industry"));
const Delegate = lazy(() => import(/* webpackChunkName: "delegate" */ "./Reg-Routes/Delegate"));
const Travels = lazy(() => import(/* webpackChunkName: "travels" */ "./Travels/Travels"));
const OfficeBearers = lazy(() => import(/* webpackChunkName: "office-bearers" */ "./OfficeBearers/OfficeBearers"));
const VerifyRegistration = lazy(() => import(/* webpackChunkName: "verify-registration" */ "./VerifyRegistration/VerifyRegistration"));
const InternationalRegistrations = lazy(
  () => import(/* webpackChunkName: "registration-international" */ "./Reg-Routes/InternationalRegistrations")
);
const Trip = lazy(() => import(/* webpackChunkName: "trip" */ "./Trip/Trip"));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "privacy-policy" */ "./Policy/PrivacyPolicy"));
const Workshop = lazy(() => import(/* webpackChunkName: "workshop" */ "./Workshop/Workshop.js"));
const CallForAbstracts = lazy(() => import(/* webpackChunkName: "call-for-abstracts" */ "./CallForAbstracts/CallForAbstracts"));
const CallForSymposium = lazy(() => import(/* webpackChunkName: "call-for-symposium" */ "./CallForSymposium/CallForSymposium"));
const SymposiumForm = lazy(() => import(/* webpackChunkName: "symposium-form" */ "./CallForSymposium/SymposiumForm"));
const CallForPanelDiscussion = lazy(
  () => import(/* webpackChunkName: "call-for-panel-discussion" */ "./CallForPanelDiscussion/CallForPanelDiscussion")
);
const PanelDiscussionForm = lazy(
  () => import(/* webpackChunkName: "panel-discussion-form" */ "./CallForPanelDiscussion/PanelDiscussionForm")
);
const CallForQuizCompetition = lazy(
  () => import(/* webpackChunkName: "call-for-quiz-competition" */ "./CallForQuizCompetition/CallForQuizCompetition")
);
const QuizForm = lazy(() => import(/* webpackChunkName: "quiz-form" */ "./CallForQuizCompetition/QuizForm"));
const Accomodation = lazy(() => import(/* webpackChunkName: "accomodation" */ "./Accomodation/Accomodation"));
const Visa = lazy(() => import(/* webpackChunkName: "visa" */ "./Visa/Visa"));
const Feedback = lazy(() => import(/* webpackChunkName: "feedback" */ "./Feedback/Feedback"));
const FeedbackForm = lazy(() => import(/* webpackChunkName: "feedback-form" */ "./Feedback/FeedbackForm"));
const ECertificate = lazy(() => import(/* webpackChunkName: "e-certificate" */ "./Feedback/ECertificate"));
const Sponsorship = lazy(() => import(/* webpackChunkName: "sponsorship" */ "./Sponsorship/Sponsorship"));

const theme = createTheme({
  palette: {
    primary: {
      main: "#132433",
    },
    secondary: {
      main: "#ffff",
    },
  },
  typography: {
    fontFamily: "DM Sans, sans-serif",
  },
});

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.customHistory = createBrowserHistory();
  }

  componentDidMount() {
    // Initialize GA4 (disable the automatic page_view so we control all hits manually)
    ReactGA.initialize("123", {
      gtagOptions: { send_page_view: false },
    });

    const trackPage = (path) => ReactGA.send({ hitType: "pageview", page: path, title: document.title });

    // Track first page load
    trackPage(window.location.pathname + window.location.search);

    // Track subsequent route changes
    this.customHistory.listen((location) => {
      trackPage(location.pathname + location.search);
    });
  }

  render = () => {
    return (
      <ThemeProvider theme={theme}>
        <Router history={this.customHistory}>
          <Suspense
            fallback={
              <div className="loader-container">
                <Loader />
              </div>
            }>
            <Switch>
              <Route exact path="/" component={Home} />
              <Route path="/committee/:name" component={Team} />
              <Route path="/office-bearers/:name" component={OfficeBearers} />
              <Route path="/faculty/" component={Faculty} />
              {/* <Route path="/partners" component={Partners} /> */}
              <Route path="/pre-conference-workshop" component={PreConferenceWorkshop} />

              <Route exact path="/abstract" component={Abstract} />
              <Route path="/scientific-program" component={ScientificProgram} />
              <Route path="/scientific-schedule" component={ScientificSchedule} />
              <Route path="/workshop" component={Workshop} />
              <Route path="/call-for-abstracts" component={CallForAbstracts} />

              {/* <Route exact path="/call-for-symposium" component={CallForSymposium} /> */}
              {/* <Route path="/call-for-symposium/form" component={SymposiumForm} /> */}
              {/* <Route exact path="/call-for-panel-discussion" component={CallForPanelDiscussion} /> */}
              {/* <Route path="/call-for-panel-discussion/form" component={PanelDiscussionForm} /> */}
              {/* <Route exact path="/call-for-quiz-competition" component={CallForQuizCompetition} /> */}
              {/* <Route path="/call-for-quiz-competition/form" component={QuizForm} /> */}
              <Route path="/contact" component={Contact} />
              <Route exact path="/feedback" component={Feedback} />
              <Route exact path="/feedback/e-certificate/cme" render={(props) => <ECertificate {...props} certificateType="CME" />} />
              <Route exact path="/feedback/e-certificate/workshop" render={(props) => <ECertificate {...props} certificateType="Workshop" />} />
              <Route exact path="/feedback/cme" render={(props) => <FeedbackForm {...props} formType="cme" />} />
              <Route exact path="/feedback/workshop" render={(props) => <FeedbackForm {...props} formType="workshop" />} />
              <Route exact path="/registration" component={Registration} />
              <Route exact path="/registration/form" component={Delegate} />
              <Route exact path="/registration/international" component={InternationalRegistrations} />
              {/* <Route exact path="/registration/staff-form" component={Staff} /> */}
              <Route exact path="/registration/industry-form/:id" component={Industry} />
              {/* <Route exact path="/registration/industry-as-delegate" component={IndustryAsDelegate} /> */}
              <Route path="/accomodation" component={Accomodation} />
              <Route path="/visa" component={Visa} />
              <Route path="/sponsorship" component={Sponsorship} />
              <Route path="/nearby-places" component={NearbyPlaces} />
              <Route path="/order-success" component={OrderSuccess} />
              <Route path="/order-failed" component={OrderFail} />
              <Route path="/trip-ayo" component={Trip} />
              <Route path="/travel-and-accomodation" component={Travels} />
              <Route path="/delegate/:delegateID" component={VerifyRegistration} />
              <Route path="/privacy-policy" component={PrivacyPolicy} />
              <Route component={PageMiss} />
            </Switch>
          </Suspense>
        </Router>
      </ThemeProvider>
    );
  };
}

export default App;
